import React, { useState, useCallback, useMemo, ReactNode } from 'react';
import { auditApi } from '@/modules/audit/api/auditApi';
import {
  clearImpersonation,
  readImpersonation,
  writeImpersonation,
  type ImpersonatedPractice,
} from '@/shared/api/impersonationStorage';
import { ImpersonationContext } from './useImpersonation';

export type { ImpersonatedPractice };

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  // Per tab (see impersonationStorage): another tab opening a practice never changes this one.
  const [impersonatedPractice, setImpersonatedPractice] = useState<ImpersonatedPractice | null>(
    readImpersonation,
  );

  const startImpersonation = useCallback((practice: ImpersonatedPractice) => {
    writeImpersonation(practice);
    setImpersonatedPractice(practice);
    // An agency user acting inside a client's account is the event the audit
    // log most needs to show, so record it at the moment it starts rather than
    // inferring it from the practice context on later page views.
    auditApi.recordImpersonation(practice.id, practice.name);
  }, []);

  const stopImpersonation = useCallback(() => {
    clearImpersonation();
    setImpersonatedPractice(null);
  }, []);

  const value = useMemo(
    () => ({ impersonatedPractice, startImpersonation, stopImpersonation }),
    [impersonatedPractice, startImpersonation, stopImpersonation],
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}
